/**
 * ProgressBar — horizontal progress indicator with optional count or percent label.
 *
 * Mockup classes: .progress-lab, .progress-num, .progress.
 * Uses role="progressbar" with aria-valuenow/min/max for accessibility.
 */

export type ProgressBarProps = {
  value: number;
  total: number;
  label?: "count" | "percent" | "none";
  ariaLabel?: string;
};

/**
 * Renders a styled progress bar with an optional text label.
 *
 * @param value    - Current progress value.
 * @param total    - Maximum value (denominator). Clamped to at least 1 internally.
 * @param label    - Display mode: "count" shows "N/T", "percent" shows "P%", "none" hides label.
 * @param ariaLabel - Accessible label for the progressbar role (defaults to "Progress").
 */
export function ProgressBar({
  value,
  total,
  label = "none",
  ariaLabel,
}: ProgressBarProps) {
  const pct = Math.min(100, Math.round((value / Math.max(total, 1)) * 100));

  let labelText: string | null = null;
  if (label === "count") {
    labelText = `${value}/${total}`;
  } else if (label === "percent") {
    labelText = `${pct}%`;
  }

  return (
    <div className="progress-lab">
      {labelText ? (
        <span className="progress-num t-mono">{labelText}</span>
      ) : null}
      <div
        className="progress"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={ariaLabel ?? "Progress"}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
